(() => {
  const VIDEO_SELECTOR = '.card__hover-video';
  const CARD_SELECTOR = '.card-wrapper';
  const SLIDER_SELECTOR = '.card__media--has-video';

  const canHover = window.matchMedia('(hover: hover)').matches;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Cards are small: 480p on phones, 720p for the larger desktop cards.
  const MAX_HEIGHT = canHover ? 720 : 480;

  // An animated WebP <img> (product without a video) stands in for the video: it
  // downloads on first hover/swipe and shows once loaded.
  const isImage = (media) => media.tagName === 'IMG';
  const pause = (media) => {
    if (!isImage(media)) media.pause();
  };

  const start = (video) => {
    if (isImage(video)) {
      if (!video.getAttribute('src')) {
        video.addEventListener('load', () => video.classList.add('is-playing'), { once: true });
        video.src = video.dataset.src;
      } else if (video.complete) {
        video.classList.add('is-playing');
      }
      return;
    }
    video.muted = true;
    if (!video.getAttribute('src')) {
      if (window.videoRendition) window.videoRendition.attach(video);
      else video.src = video.dataset.src;
    }
    const promise = video.play();
    if (promise) promise.catch(() => {});
  };

  const stop = (video) => {
    video.classList.remove('is-playing');
    if (isImage(video)) return;
    video.pause();
    video.currentTime = 0;
  };

  // Runs shortly before a card scrolls into view: picks the rendition (reads a
  // tiny playlist, no video bytes) and, on touch devices, sets the poster (a
  // small image of the first frame) so the video slide never shows up empty.
  const prepare = (video) => {
    if (isImage(video)) return;
    if (!canHover && !video.poster && video.dataset.poster) video.poster = video.dataset.poster;
    if (window.videoRendition) window.videoRendition.prepare(video, MAX_HEIGHT);
  };

  const observeCards = (callback) => {
    if (!('IntersectionObserver' in window)) return;
    const observeAll = (root) => root.querySelectorAll?.(SLIDER_SELECTOR).forEach(callback);
    observeAll(document);
    new MutationObserver((mutations) =>
      mutations.forEach((m) => m.addedNodes.forEach((node) => node.nodeType === 1 && observeAll(node)))
    ).observe(document.body, { childList: true, subtree: true });
  };

  const preloader =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach(({ target, isIntersecting }) => {
              if (!isIntersecting) return;
              preloader.unobserve(target);
              const video = target.querySelector(VIDEO_SELECTOR);
              if (video) prepare(video);
            });
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  if (canHover) {
    if (reduceMotion) return;
    observeCards((el) => preloader.observe(el));

    document.addEventListener(
      'playing',
      (event) => {
        if (event.target.matches?.(VIDEO_SELECTOR)) event.target.classList.add('is-playing');
      },
      true
    );

    document.addEventListener('pointerover', (event) => {
      const card = event.target.closest?.(CARD_SELECTOR);
      if (!card || card.contains(event.relatedTarget)) return;
      const video = card.querySelector(VIDEO_SELECTOR);
      if (video) start(video);
    });

    document.addEventListener('pointerout', (event) => {
      const card = event.target.closest?.(CARD_SELECTOR);
      if (!card || card.contains(event.relatedTarget)) return;
      const video = card.querySelector(VIDEO_SELECTOR);
      if (video) stop(video);
    });
    return;
  }

  // Touch devices: the card media is a two-slide swiper (image, video). The
  // video itself downloads once a swipe begins.
  const updateDots = (slider) => {
    const index = Math.round(slider.scrollLeft / Math.max(slider.clientWidth, 1));
    const dots = slider.parentElement?.querySelectorAll('.card__media-dots span') || [];
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    return index;
  };

  const timers = new WeakMap();
  document.addEventListener(
    'scroll',
    (event) => {
      const slider = event.target;
      if (!(slider instanceof Element) || !slider.matches(SLIDER_SELECTOR)) return;

      const video = slider.querySelector(VIDEO_SELECTOR);
      if (video && slider.scrollLeft > 0) {
        prepare(video);
        if (isImage(video) || video.paused) start(video);
      }

      window.clearTimeout(timers.get(slider));
      timers.set(
        slider,
        window.setTimeout(() => {
          if (updateDots(slider) === 0 && video) pause(video);
        }, 80)
      );
    },
    true
  );

  // iOS Safari (Low Power Mode, in-app browsers) only allows play() inside a real
  // user gesture; a scroll event is not one, but the touchend of the swipe is.
  document.addEventListener(
    'touchend',
    (event) => {
      const slider = event.target.closest?.(SLIDER_SELECTOR);
      if (!slider || slider.scrollLeft <= 0) return;
      const video = slider.querySelector(VIDEO_SELECTOR);
      if (!video) return;
      prepare(video);
      start(video);
    },
    { capture: true, passive: true }
  );

  // The slider sits above the card link overlay, so a tap on it navigates explicitly.
  document.addEventListener('click', (event) => {
    const slider = event.target.closest?.(SLIDER_SELECTOR);
    if (!slider) return;
    const link = slider.closest(CARD_SELECTOR)?.querySelector('.card__heading a[href]');
    if (link) window.location.href = link.href;
  });

  // Pause videos that scroll out of view with the page.
  const visibility =
    'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach(({ target, isIntersecting }) => {
            const video = target.querySelector(VIDEO_SELECTOR);
            if (!video) return;
            if (!isIntersecting) pause(video);
            else if (target.scrollLeft >= target.clientWidth / 2) start(video);
          });
        })
      : null;

  observeCards((el) => {
    preloader.observe(el);
    visibility.observe(el);
  });
})();
